/**
 * Route tree — port of rent_management_app/lib/core/routes/app_router.dart.
 *
 * Exported as a plain `RouteObject[]` rather than a built router so that
 * main.tsx can wrap it in `createBrowserRouter` while tests wrap the SAME tree
 * in `createMemoryRouter`. Building the router here would make the redirect
 * logic untestable without a real browser history.
 *
 * Structural differences from GoRouter, and why:
 *
 *   - GoRouter uses ONE top-level `redirect` callback that string-matches the
 *     target location (app_router.dart:61-93). React Router has no equivalent
 *     hook, so the same rules are expressed as guard components wrapping route
 *     branches. The rules are unchanged; only where they live moved. The gain
 *     is that "which routes are public" is now structural instead of a
 *     `startsWith('/public')` test that has to track the route table by hand.
 *
 *   - There is no `/splash` route. It exists in Flutter because
 *     flutter_secure_storage is async, so the app cannot know whether it has a
 *     session until after first frame. localStorage is synchronous, so the
 *     session is already resolved when the tree first renders (see
 *     restoreSession in src/auth/AuthContext.tsx) and a splash route would only
 *     add a redirect hop.
 *
 *   - Flutter's `/auth/register` is unreachable while logged out
 *     (app_router.dart:80-82 bounces an unauthenticated visitor to
 *     `/auth/login`), which effectively disables public signup in the app. That
 *     rule is NOT ported: the backend explicitly supports unauthenticated
 *     self-registration and forces the new account to TENANT
 *     (refactor/backend/src/modules/auth/routes.ts:154-158), so the endpoint is
 *     built for a caller Flutter never lets exist. This is a deliberate
 *     divergence — flagged, not silent. If public signup is meant to be closed,
 *     it should be closed at the BACKEND (where it is actually enforceable)
 *     rather than by a client-side redirect, and applied to both clients.
 */
import type { RouteObject } from 'react-router-dom';

import { PublicLayout } from '../layouts/PublicLayout';
import { AuthLayout } from '../layouts/AuthLayout';
import { LandlordShell } from '../layouts/LandlordShell';
import { AgentShell } from '../layouts/AgentShell';
import { TenantShell } from '../layouts/TenantShell';
import { AdminShell } from '../layouts/AdminShell';

import { RequireAuth } from './guards/RequireAuth';
import { RequireRole } from './guards/RequireRole';
import { RedirectIfAuthenticated } from './guards/RedirectIfAuthenticated';

import { LandingPage } from '../pages/public/LandingPage';
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { LandlordHome } from '../pages/landlord/LandlordHome';
import { AgentHome } from '../pages/agent/AgentHome';
import { TenantHome } from '../pages/tenant/TenantHome';
import { AdminHome } from '../pages/admin/AdminHome';
import { NotFoundPage } from '../pages/NotFoundPage';

import { PropertyListPage } from '../pages/properties/PropertyListPage';
import { PropertyDetailPage } from '../pages/properties/PropertyDetailPage';
import { PropertyFormPage } from '../pages/properties/PropertyFormPage';
import { PublicPropertyBrowsePage } from '../pages/properties/PublicPropertyBrowsePage';
import { PublicPropertyDetailPage } from '../pages/properties/PublicPropertyDetailPage';
import { PublicPropertyMapPage } from '../pages/properties/PublicPropertyMapPage';

import { TenantListPage } from '../pages/tenants/TenantListPage';
import { TenantFormPage } from '../pages/tenants/TenantFormPage';
import { TenantDetailPage } from '../pages/tenants/TenantDetailPage';
import { LeaseListPage } from '../pages/tenants/LeaseListPage';
import { LeaseFormPage } from '../pages/tenants/LeaseFormPage';

import { InvoiceListPage } from '../pages/payments/InvoiceListPage';
import { InvoiceDetailPage } from '../pages/payments/InvoiceDetailPage';
import { InvoiceFormPage } from '../pages/payments/InvoiceFormPage';
import { RecurringChargeListPage } from '../pages/payments/RecurringChargeListPage';
import { EvictionNoticeListPage } from '../pages/tenants/EvictionNoticeListPage';
import { PaymentListPage } from '../pages/payments/PaymentListPage';
import { LedgerPage } from '../pages/payments/LedgerPage';
import { UpcomingPaymentsPage } from '../pages/payments/UpcomingPaymentsPage';
import { OverduePaymentsPage } from '../pages/payments/OverduePaymentsPage';

import { MaintenanceListPage } from '../pages/maintenance/MaintenanceListPage';
import { MaintenanceDetailPage } from '../pages/maintenance/MaintenanceDetailPage';
import { MaintenanceFormPage } from '../pages/maintenance/MaintenanceFormPage';

import { ExpenseListPage } from '../pages/expenses/ExpenseListPage';
import { ExpenseFormPage } from '../pages/expenses/ExpenseFormPage';
import { ExpenseSummaryPage } from '../pages/expenses/ExpenseSummaryPage';

import { ReportsDashboardPage } from '../pages/reports/ReportsDashboardPage';
import { RentRollPage } from '../pages/reports/RentRollPage';
import { MaintenanceReportPage } from '../pages/reports/MaintenanceReportPage';
import { FinancialReportPage } from '../pages/reports/FinancialReportPage';
import { TaxSummaryPage } from '../pages/reports/TaxSummaryPage';

import { OffboardingListPage } from '../pages/offboarding/OffboardingListPage';
import { OffboardingDetailPage } from '../pages/offboarding/OffboardingDetailPage';
import { OffboardingInitiatePage } from '../pages/offboarding/OffboardingInitiatePage';
import { InspectionItemFormPage } from '../pages/offboarding/InspectionItemFormPage';

import { ConversationListPage } from '../pages/messaging/ConversationListPage';
import { ConversationDetailPage } from '../pages/messaging/ConversationDetailPage';
import { NewConversationPage } from '../pages/messaging/NewConversationPage';
import { BroadcastListPage } from '../pages/messaging/BroadcastListPage';
import { BroadcastFormPage } from '../pages/messaging/BroadcastFormPage';

import { ProfilePage } from '../pages/profile/ProfilePage';
import { NotificationsPage } from '../pages/notifications/NotificationsPage';
import { UserListPage } from '../pages/admin/UserListPage';

/**
 * Shared by the landlord and agent branches — both trees expose the same
 * property/tenant/lease management surface; only the RequireRole gate above
 * each branch (and the backend's per-role scoping) differs.
 */
const propertyRoutes: RouteObject[] = [
  {
    path: 'properties',
    children: [
      { index: true, element: <PropertyListPage /> },
      { path: 'new', element: <PropertyFormPage /> },
      { path: ':id', element: <PropertyDetailPage /> },
      { path: ':id/edit', element: <PropertyFormPage /> },
    ],
  },
];

const tenantRoutes: RouteObject[] = [
  {
    path: 'tenants',
    children: [
      { index: true, element: <TenantListPage /> },
      { path: 'new', element: <TenantFormPage /> },
      {
        path: 'leases',
        children: [
          { index: true, element: <LeaseListPage /> },
          { path: 'new', element: <LeaseFormPage /> },
          { path: ':id/edit', element: <LeaseFormPage /> },
        ],
      },
      { path: ':id', element: <TenantDetailPage /> },
      { path: ':id/edit', element: <TenantFormPage /> },
    ],
  },
];

/**
 * Billing/rent/payments — same page components across all three role trees;
 * only the URL SEGMENT differs (landlord: `rent`, agent: `billing`, tenant:
 * `payments` — see the shells' NAV_ITEMS) and whether the create/edit routes
 * are mounted at all. `InvoiceListPage`/`InvoiceDetailPage` hide their own
 * write affordances by role (see those files), so the tenant branch simply
 * never mounts `new`/`:id/edit` rather than relying only on that in-page
 * gating — a stray link can never 404 into a route that would 403 anyway.
 */
function billingRoutes(segment: string, options: { manager: boolean }): RouteObject {
  const children: RouteObject[] = [
    { index: true, element: <InvoiceListPage /> },
    { path: 'payments', element: <PaymentListPage /> },
    { path: 'ledger', element: <LedgerPage /> },
    { path: 'upcoming', element: <UpcomingPaymentsPage /> },
    { path: 'overdue', element: <OverduePaymentsPage /> },
  ];

  if (options.manager) {
    children.push({ path: 'new', element: <InvoiceFormPage /> });
    // Node-only feature, no Django counterpart — see RecurringChargeListPage.tsx.
    children.push({ path: 'recurring-charges', element: <RecurringChargeListPage /> });
  }

  children.push({ path: ':id', element: <InvoiceDetailPage /> });

  if (options.manager) {
    children.push({ path: ':id/edit', element: <InvoiceFormPage /> });
  }

  return { path: segment, children };
}

/**
 * Shared by all three role trees. `maintenance/routes.ts` gates writes at the
 * ROW level, not the role level (plain `IsAuthenticated` on the whole CRUD
 * surface — a tenant may PATCH/DELETE their own request), so there is no
 * "manager" vs "reduced" route split the way `billingRoutes` has: every role
 * gets list/new/detail/edit, and `MaintenanceDetailPage` itself hides the
 * manager-only assign/complete actions by role. Mounting one identical array
 * for landlord, agent AND tenant is therefore correct here, not an oversight.
 */
const maintenanceRoutes: RouteObject[] = [
  {
    path: 'maintenance',
    children: [
      { index: true, element: <MaintenanceListPage /> },
      { path: 'new', element: <MaintenanceFormPage /> },
      { path: ':id', element: <MaintenanceDetailPage /> },
      { path: ':id/edit', element: <MaintenanceFormPage /> },
    ],
  },
];

/**
 * Landlord + agent only — `expenses/routes.ts` gates every write with
 * `assertManagerRole`, and the tenant shell has no expenses nav/route at all.
 */
const expenseRoutes: RouteObject[] = [
  {
    path: 'expenses',
    children: [
      { index: true, element: <ExpenseListPage /> },
      { path: 'new', element: <ExpenseFormPage /> },
      { path: 'summary', element: <ExpenseSummaryPage /> },
      { path: ':id/edit', element: <ExpenseFormPage /> },
    ],
  },
];

/**
 * Landlord + agent only. `ReportsDashboardPage` itself still branches on
 * `isTenantDashboard`, but that branch is unreachable from here — only a
 * manager role ever mounts this tree, so `getDashboard()` always returns the
 * manager shape in practice. The tenant's own dashboard summary is a future
 * `TenantHome.tsx` concern, not a route under this tree.
 */
const reportRoutes: RouteObject[] = [
  {
    path: 'reports',
    children: [
      { index: true, element: <ReportsDashboardPage /> },
      { path: 'rent-roll', element: <RentRollPage /> },
      { path: 'maintenance', element: <MaintenanceReportPage /> },
      { path: 'financial', element: <FinancialReportPage /> },
      // Node-only, no Django counterpart — see TaxSummaryPage.tsx.
      { path: 'tax-summary', element: <TaxSummaryPage /> },
    ],
  },
];

/**
 * Landlord + agent only — `offboarding/routes.ts` gates every write with
 * `assertManagerRole`, and the `offboardingLease` scope has NO tenant branch
 * (see the route file's top comment), which is what stops a tenant from
 * initiating their own offboarding through `initiate`. There is no tenant
 * nav item and no product need shown backend-side for a tenant-facing
 * offboarding view, so no tenant route is mounted either — not even
 * read-only.
 *
 * `:offboardingId/inspections/new` is nested under the LIST branch (a
 * sibling of `:id`, not a child of it) purely so its own `:id`-shaped param
 * name doesn't collide with the detail route's `:id` — React Router matches
 * whichever pattern is more specific first either way.
 */
const offboardingRoutes: RouteObject[] = [
  {
    path: 'offboarding',
    children: [
      { index: true, element: <OffboardingListPage /> },
      { path: 'initiate', element: <OffboardingInitiatePage /> },
      { path: ':id', element: <OffboardingDetailPage /> },
      { path: ':offboardingId/inspections/new', element: <InspectionItemFormPage /> },
    ],
  },
];

/**
 * Landlord + agent + tenant — `messaging/routes.ts` uses plain
 * `IsAuthenticated` with per-conversation scoping (`buildScope(user,
 * 'conversation')` is role-independent: every authenticated caller reads/
 * writes only conversations they participate in). Mounted identically for
 * all three role trees, same reasoning as `maintenanceRoutes`.
 */
const messagingRoutes: RouteObject[] = [
  {
    path: 'messages',
    children: [
      { index: true, element: <ConversationListPage /> },
      { path: 'new', element: <NewConversationPage /> },
      { path: ':id', element: <ConversationDetailPage /> },
    ],
  },
];

/**
 * Landlord + agent + tenant — Node-only feature, no Django counterpart. Same
 * reasoning as `messagingRoutes`: `evictionNoticeScope` (rbac.ts) is
 * role-independent read access (a TENANT sees only notices on their own
 * lease), so this mounts identically for all three role trees.
 * `EvictionNoticeListPage` itself hides the manager-only withdraw action by
 * role.
 */
const evictionNoticeRoutes: RouteObject[] = [
  { path: 'eviction-notices', element: <EvictionNoticeListPage /> },
];

/**
 * Landlord + agent only. `BroadcastMessageViewSet` gates EVERY method
 * (including GET) to manager roles server-side — a TENANT gets 403 even
 * trying to list broadcasts they might be a target of, so no tenant route or
 * nav item is mounted for this tree; see the route file's top comment.
 */
const broadcastRoutes: RouteObject[] = [
  {
    path: 'broadcasts',
    children: [
      { index: true, element: <BroadcastListPage /> },
      { path: 'new', element: <BroadcastFormPage /> },
      { path: ':id/edit', element: <BroadcastFormPage /> },
    ],
  },
];

export const routes: RouteObject[] = [
  // --- public ---------------------------------------------------------------
  {
    element: <PublicLayout />,
    children: [
      {
        // Landing only. `/public/*` stays reachable while authenticated —
        // app_router.dart's redirect bounces `/` but not `/public/...` (L84).
        element: <RedirectIfAuthenticated />,
        children: [{ index: true, element: <LandingPage /> }],
      },
      {
        path: 'public',
        children: [
          {
            path: 'properties',
            children: [
              { index: true, element: <PublicPropertyBrowsePage /> },
              // Static segment before ':id' — React Router already prioritises
              // static matches over dynamic params regardless of order, but
              // keeping 'map' first here documents that intent explicitly.
              { path: 'map', element: <PublicPropertyMapPage /> },
              { path: ':id', element: <PublicPropertyDetailPage /> },
            ],
          },
        ],
      },
      // TODO(feature phase): /public/terms, /public/privacy (app_router.dart:456-476).
    ],
  },

  // --- auth -----------------------------------------------------------------
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        element: <RedirectIfAuthenticated />,
        children: [{ path: 'login', element: <LoginPage /> }],
      },
      {
        // app_router.dart:72-78 — an authenticated AGENT/SUPERAGENT keeps
        // access to the register form because that is how they create users.
        element: <RedirectIfAuthenticated allowManagerRoles />,
        children: [{ path: 'register', element: <RegisterPage /> }],
      },
    ],
  },

  // --- authenticated, role-scoped -------------------------------------------
  {
    element: <RequireAuth />,
    children: [
      {
        path: 'landlord',
        element: <RequireRole allowed={['LANDLORD', 'SUPERAGENT']} />,
        children: [
          {
            element: <LandlordShell />,
            children: [
              { index: true, element: <LandlordHome /> },
              ...propertyRoutes,
              ...tenantRoutes,
              billingRoutes('rent', { manager: true }),
              ...maintenanceRoutes,
              ...expenseRoutes,
              ...reportRoutes,
              ...offboardingRoutes,
              ...messagingRoutes,
              ...evictionNoticeRoutes,
              ...broadcastRoutes,
              // No dedicated 'agents' route: LandlordShell's nav used to link
              // to one that was never built (a real 404 in production —
              // 'Agents' was Phase 3 scaffolding for a page that never
              // shipped). Removed rather than built, since 'users' already
              // covers the same need (list/filter/create by role, AGENT
              // included) and building a second, redundant surface wasn't
              // the fix a broken-nav-link bug report called for.
              { path: 'users', element: <UserListPage /> },
              { path: 'profile', element: <ProfilePage /> },
            ],
          },
        ],
      },
      {
        path: 'agent',
        element: <RequireRole allowed={['AGENT']} />,
        children: [
          {
            element: <AgentShell />,
            children: [
              { index: true, element: <AgentHome /> },
              ...propertyRoutes,
              ...tenantRoutes,
              billingRoutes('billing', { manager: true }),
              ...maintenanceRoutes,
              ...expenseRoutes,
              ...reportRoutes,
              ...offboardingRoutes,
              ...messagingRoutes,
              ...evictionNoticeRoutes,
              ...broadcastRoutes,
              // Same profile/settings content as the landlord tree's
              // 'profile' segment — only the URL segment differs, matching
              // the label already on AgentShell's nav ('Settings').
              { path: 'settings', element: <ProfilePage /> },
            ],
          },
        ],
      },
      {
        path: 'tenant',
        element: <RequireRole allowed={['TENANT']} />,
        children: [
          {
            element: <TenantShell />,
            children: [
              { index: true, element: <TenantHome /> },
              billingRoutes('payments', { manager: false }),
              ...maintenanceRoutes,
              ...messagingRoutes,
              ...evictionNoticeRoutes,
              { path: 'profile', element: <ProfilePage /> },
              { path: 'notifications', element: <NotificationsPage /> },
            ],
          },
        ],
      },
      {
        // No Flutter counterpart — see the note on src/layouts/AdminShell.tsx.
        path: 'admin',
        element: <RequireRole allowed={['SUPERAGENT']} />,
        children: [
          {
            element: <AdminShell />,
            children: [
              { index: true, element: <AdminHome /> },
              // Same component as the landlord tree's 'users' route — see
              // UserListPage's own header comment on why one component
              // serves both mount points.
              { path: 'users', element: <UserListPage /> },
            ],
          },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
];

export default routes;
